/**
 * Public Report Settings
 * Centralized settings for public issue reporting via QR codes
 *
 * Combines:
 * - Branding settings (logo for QR PDF)
 * - The customer's privacy policy for codes placed in public places
 * - Building picker to generate new QR codes
 * - List of all buildings with active QR codes
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Alert, Form, Spinner, Modal, Table } from 'react-bootstrap';
import { QrCode, Download, RefreshCw, Eye, Trash2, Plus, Users } from 'lucide-react';
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import { publicReportApi } from '../../api/publicReportApi';
import { publicReportBrandingApi } from '../../api/publicReportBrandingApi';
import { PublicReportBranding, ActiveTokenWithBuilding } from '../../types/publicReport';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { downloadQrPdf, openQrPdf, urlToDataUrl, reencodeImageAsCleanPng } from '../../utils/qrPdfGenerator';
import { SectionLoader } from '../../Components/LoadingSpinner';
import { FileInputControl } from '../../Components/FileInputControl';
import { usePublicReportPrivacy } from '../../hooks/publicReport/usePublicReportPrivacy';
import PublicReportPrivacyCard from './PublicReportPrivacyCard';
import PublicPlacementFields, {
    PublicPlacementValue,
    INTERNAL_PLACEMENT,
    isPlacementComplete,
} from './PublicPlacementFields';

/** localStorage key: the admin dismissed the hint about codes created before public placement existed */
const PLACEMENT_HINT_DISMISSED_KEY = 'publicReport.placementHintDismissed';

const readHintDismissed = (): boolean => {
    try {
        return localStorage.getItem(PLACEMENT_HINT_DISMISSED_KEY) === '1';
    } catch {
        return false;
    }
};

const PublicReportSettings: React.FC = () => {
    // Branding state
    const [brandingConfig, setBrandingConfig] = useState<PublicReportBranding>({});
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [isLogoDragOver, setIsLogoDragOver] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [savingBranding, setSavingBranding] = useState(false);

    // Token list state
    const [activeTokens, setActiveTokens] = useState<ActiveTokenWithBuilding[]>([]);
    const [loadingTokens, setLoadingTokens] = useState(true);

    // Building picker state
    const { buildings, loadingSummaries } = useBuildingsData();
    const [selectedBuildingKey, setSelectedBuildingKey] = useState('');
    const [createPlacement, setCreatePlacement] = useState<PublicPlacementValue>(INTERNAL_PLACEMENT);
    const [creatingToken, setCreatingToken] = useState(false);

    // Privacy policy for public codes
    const privacy = usePublicReportPrivacy();
    const [placementHintDismissed, setPlacementHintDismissed] = useState(readHintDismissed);
    // "Planerar ni att sätta upp QR-koder på allmän plats?" Not stored: it starts
    // ticked when a policy or a public code already exists, and only shows or
    // hides the policy section (unticking never removes the policy)
    const [plansPublicCodes, setPlansPublicCodes] = useState<boolean | null>(null);

    // Modal state for managing individual QR codes
    const [selectedToken, setSelectedToken] = useState<ActiveTokenWithBuilding | null>(null);
    const [showManageModal, setShowManageModal] = useState(false);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [logoDataUrl, setLogoDataUrl] = useState<string | undefined>(undefined);

    // Confirmation modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState<'revoke' | 'regenerate' | null>(null);
    const [regeneratePlacement, setRegeneratePlacement] = useState<PublicPlacementValue>(INTERNAL_PLACEMENT);
    const [actionLoading, setActionLoading] = useState(false);

    // General UI state
    const [loadingBranding, setLoadingBranding] = useState(true);
    const [message, setMessage] = useState<{ type: 'success' | 'danger', text: string } | null>(null);

    // Load branding configuration
    useEffect(() => {
        const loadBranding = async () => {
            try {
                const data = await publicReportBrandingApi.getBranding();
                setBrandingConfig(data);
                setLogoPreview(data.logoUrl || null);
                if (data.logoUrl) {
                    try {
                        const dataUrl = await urlToDataUrl(data.logoUrl);
                        // Validate it's actually a valid data URL for images
                        if (dataUrl && dataUrl.startsWith('data:image/')) {
                            // Re-encode through canvas to ensure pdfmake compatibility
                            const cleanDataUrl = await reencodeImageAsCleanPng(dataUrl);
                            setLogoDataUrl(cleanDataUrl);
                        } else {
                            console.warn('Logo URL conversion did not produce valid data URL');
                        }
                    } catch (logoError) {
                        console.warn('Could not convert logo to data URL:', logoError);
                        // Continue without logo - non-critical
                    }
                }
            } catch (error) {
                console.error('Error loading public report branding config:', error);
                setMessage({ type: 'danger', text: 'Kunde inte ladda konfiguration' });
            } finally {
                setLoadingBranding(false);
            }
        };

        loadBranding();
    }, []);

    // Load active tokens
    const loadActiveTokens = useCallback(async () => {
        setLoadingTokens(true);
        try {
            const response = await publicReportApi.getAllActiveTokens();
            setActiveTokens(response.tokens);
        } catch (error) {
            console.error('Error loading active tokens:', error);
            setMessage({ type: 'danger', text: 'Kunde inte ladda QR-koder' });
        } finally {
            setLoadingTokens(false);
        }
    }, []);

    useEffect(() => {
        loadActiveTokens();
    }, [loadActiveTokens]);

    useEffect(() => {
        if (plansPublicCodes !== null || privacy.loading || loadingTokens) return;
        setPlansPublicCodes(Boolean(privacy.policy) || activeTokens.some(t => t.publicPlacement));
    }, [plansPublicCodes, privacy.loading, privacy.policy, loadingTokens, activeTokens]);

    // Marking a code as public reveals the policy section it relates to
    const handleCreatePlacementChange = (value: PublicPlacementValue) => {
        setCreatePlacement(value);
        if (value.publicPlacement) setPlansPublicCodes(true);
    };

    const handleRegeneratePlacementChange = (value: PublicPlacementValue) => {
        setRegeneratePlacement(value);
        if (value.publicPlacement) setPlansPublicCodes(true);
    };

    // Logo file change handler
    const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setMessage({ type: 'danger', text: 'Vänligen välj en bildfil' });
                return;
            }
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    // Drag and drop handlers
    const logoDragHandlers = {
        onDragOver: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsLogoDragOver(true);
        },
        onDragLeave: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setIsLogoDragOver(false);
            }
        },
        onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsLogoDragOver(false);

            const files = Array.from(e.dataTransfer.files);
            const imageFile = files.find(f => f.type.startsWith('image/'));
            if (!imageFile) {
                setMessage({ type: 'danger', text: 'Vänligen välj en bildfil' });
                return;
            }

            setLogoFile(imageFile);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(imageFile);
        }
    };

    const openImageInFancybox = (imageUrl: string, caption: string) => {
        Fancybox.show([{
            src: imageUrl,
            caption: caption
        }], {
            showClass: "fancybox-fadeIn"
        });
    };

    const handleRemoveLogo = () => {
        setShowRemoveModal(true);
    };

    const confirmRemove = async () => {
        setShowRemoveModal(false);
        setSavingBranding(true);
        setMessage(null);

        try {
            const updatedConfig = await publicReportBrandingApi.removeLogo(brandingConfig.logoStoragePath);
            setBrandingConfig(updatedConfig);
            setLogoPreview(null);
            setLogoFile(null);
            setLogoDataUrl(undefined);
            setMessage({ type: 'success', text: 'Logotyp borttagen' });
        } catch (error) {
            console.error('Error removing logo:', error);
            setMessage({ type: 'danger', text: 'Kunde inte ta bort logotypen' });
        } finally {
            setSavingBranding(false);
        }
    };

    const handleSaveBranding = async () => {
        if (!logoFile) return;

        setSavingBranding(true);
        setMessage(null);

        try {
            const updatedConfig = await publicReportBrandingApi.uploadLogo(logoFile);
            setBrandingConfig(updatedConfig);
            // Re-encode the logo through canvas to ensure pdfmake compatibility
            if (logoPreview && logoPreview.startsWith('data:image/')) {
                try {
                    const cleanDataUrl = await reencodeImageAsCleanPng(logoPreview);
                    setLogoDataUrl(cleanDataUrl);
                } catch (err) {
                    console.warn('Could not re-encode logo:', err);
                    setLogoDataUrl(logoPreview);
                }
            }
            setLogoFile(null);
            setMessage({ type: 'success', text: 'Logotyp sparad!' });
        } catch (error) {
            console.error('Error saving logo:', error);
            const errorMessage = error instanceof Error ? error.message : 'Kunde inte spara logotypen';
            setMessage({ type: 'danger', text: errorMessage });
        } finally {
            setSavingBranding(false);
        }
    };

    // Building picker - create QR code
    const handleCreateQrCode = async () => {
        if (!selectedBuildingKey || !isPlacementComplete(createPlacement)) return;

        setCreatingToken(true);
        setMessage(null);

        try {
            await publicReportApi.createToken(selectedBuildingKey, createPlacement);
            setSelectedBuildingKey('');
            setCreatePlacement(INTERNAL_PLACEMENT);
            setMessage({ type: 'success', text: 'QR-kod skapad!' });
            await loadActiveTokens();
        } catch (error) {
            console.error('Error creating token:', error);
            setMessage({ type: 'danger', text: 'Kunde inte skapa QR-kod' });
        } finally {
            setCreatingToken(false);
        }
    };

    // Token management - open modal
    const handleOpenManageModal = (token: ActiveTokenWithBuilding) => {
        setSelectedToken(token);
        setShowManageModal(true);
    };

    // PDF handlers
    const handleDownloadPdf = async () => {
        if (!selectedToken) return;

        setGeneratingPdf(true);
        try {
            await downloadQrPdf({
                url: selectedToken.reportUrl,
                buildingName: selectedToken.buildingName,
                buildingAddress: selectedToken.buildingAddress,
                logoDataUrl,
            });
        } catch (err) {
            console.error('Error generating PDF:', err);
            setMessage({ type: 'danger', text: 'Kunde inte generera PDF' });
        } finally {
            setGeneratingPdf(false);
        }
    };

    const handlePreviewPdf = async () => {
        if (!selectedToken) return;

        setGeneratingPdf(true);
        try {
            await openQrPdf({
                url: selectedToken.reportUrl,
                buildingName: selectedToken.buildingName,
                buildingAddress: selectedToken.buildingAddress,
                logoDataUrl,
            });
        } catch (err) {
            console.error('Error generating PDF:', err);
            setMessage({ type: 'danger', text: 'Kunde inte generera förhandsgranskning' });
        } finally {
            setGeneratingPdf(false);
        }
    };

    // Regenerate token
    const handleRegenerateClick = () => {
        // A new code is a new creation: ask again, prefilled from the old code
        setRegeneratePlacement({ publicPlacement: selectedToken?.publicPlacement === true, acknowledged: false });
        setConfirmAction('regenerate');
        setShowConfirmModal(true);
    };

    // Revoke token
    const handleRevokeClick = () => {
        setConfirmAction('revoke');
        setShowConfirmModal(true);
    };

    // Handle confirmation
    const handleConfirm = async () => {
        if (!selectedToken) return;
        if (confirmAction === 'regenerate' && !isPlacementComplete(regeneratePlacement)) return;

        setShowConfirmModal(false);
        setActionLoading(true);

        try {
            if (confirmAction === 'regenerate') {
                await publicReportApi.createToken(selectedToken.buildingKey, regeneratePlacement);
                setMessage({ type: 'success', text: 'Ny QR-kod har skapats' });
            } else if (confirmAction === 'revoke') {
                await publicReportApi.revokeToken(selectedToken.token);
                setMessage({ type: 'success', text: 'QR-kod har tagits bort' });
            }
            setShowManageModal(false);
            setSelectedToken(null);
            await loadActiveTokens();
        } catch (error) {
            console.error('Error:', error);
            setMessage({ type: 'danger', text: confirmAction === 'revoke' ? 'Kunde inte ta bort QR-koden' : 'Kunde inte generera ny QR-kod' });
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    const dismissPlacementHint = () => {
        setPlacementHintDismissed(true);
        try {
            localStorage.setItem(PLACEMENT_HINT_DISMISSED_KEY, '1');
        } catch {
            // Private mode or blocked storage: the hint just comes back next time
        }
    };

    const publicCodeCount = activeTokens.filter(t => t.publicPlacement).length;
    const showPlacementHint = !placementHintDismissed && !loadingTokens && activeTokens.some(t => !t.publicPlacement);

    // Get buildings that don't have active tokens yet
    const availableBuildings = buildings?.filter(b =>
        !activeTokens.some(t => t.buildingKey === b.key)
    ).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sv')) || [];

    if (loadingBranding || loadingSummaries || privacy.loading) {
        return <SectionLoader />;
    }

    return (
        <div className="public-report-settings" style={{ maxWidth: '1000px' }}>
            <div className="mb-4">
                <p className="text-muted" style={{ fontSize: '14px', marginBottom: 0 }}>
                    Hantera QR-koder för publik felanmälan. Besökare kan skanna QR-koden för att rapportera fel.
                </p>
            </div>

            {message && (
                <Alert
                    variant={message.type}
                    dismissible
                    onClose={() => setMessage(null)}
                    className="mb-4"
                >
                    {message.text}
                </Alert>
            )}

            {privacy.loadError && (
                <Alert variant="danger" className="mb-4">{privacy.loadError}</Alert>
            )}

            {showPlacementHint && (
                <Alert variant="info" dismissible onClose={dismissPlacementHint} className="mb-4">
                    Sitter någon av era QR-koder på en allmän plats? Generera en ny kod, markera
                    "Placeras på allmän plats" och lägg till er integritetspolicy.
                </Alert>
            )}

            {/* Branding Card */}
            <Card className="std-box dropshadow mb-4" style={{ border: 'none' }}>
                <Card.Body style={{ padding: '20px' }}>
                    <h5 style={{ fontSize: '16px', fontWeight: 500, marginBottom: '16px' }}>Logotyp för QR-kod PDF</h5>
                    <div className="d-flex gap-4 align-items-start flex-wrap">
                        <Card
                            style={{
                                border: isLogoDragOver ? '2px dashed #007bff' : '1px solid #dee2e6',
                                width: '250px',
                                backgroundColor: isLogoDragOver ? '#f0f8ff' : 'white',
                                transition: 'all 0.2s ease'
                            }}
                            {...logoDragHandlers}
                        >
                            <Card.Body style={{ padding: '12px' }}>
                                {logoPreview ? (
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        onClick={() => openImageInFancybox(logoPreview, 'Logotyp')}
                                        style={{
                                            width: '100%',
                                            height: '120px',
                                            backgroundColor: '#ffffff',
                                            borderRadius: '6px',
                                            border: '1px solid #e0e0e0',
                                            padding: '12px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <img
                                            src={logoPreview}
                                            alt="Logo preview"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '100%',
                                                objectFit: 'contain',
                                                pointerEvents: 'none'
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{
                                            width: '100%',
                                            height: '120px',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '6px',
                                            border: '2px dashed #dee2e6',
                                            color: '#6c757d',
                                            fontSize: '12px'
                                        }}
                                    >
                                        Ingen logotyp vald
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                            <div className="mb-2">
                                <FileInputControl
                                    id="logo-file-input"
                                    fileName={logoFile?.name}
                                    onChange={handleLogoFileChange}
                                    disabled={savingBranding}
                                />
                            </div>
                            <Form.Text className="text-muted d-block mb-3" style={{ fontSize: '14px' }}>
                                Visas överst på utskrivbara QR-kod PDF:er. Rekommenderad bredd: 300-600px.
                            </Form.Text>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleSaveBranding}
                                    disabled={savingBranding || !logoFile}
                                >
                                    {savingBranding ? (
                                        <><Spinner animation="border" size="sm" className="me-2" />Sparar...</>
                                    ) : (
                                        'Spara logotyp'
                                    )}
                                </Button>
                                {logoPreview && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={handleRemoveLogo}
                                        disabled={savingBranding}
                                    >
                                        Ta bort
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* Privacy policy for public codes */}
            <Card className="std-box dropshadow mb-4" style={{ border: 'none' }}>
                <Card.Body style={{ padding: '20px' }}>
                    <Form.Check
                        type="checkbox"
                        id="plans-public-codes"
                        label="Planerar ni att sätta upp QR-koder på platser som allmänheten har tillgång till?"
                        checked={plansPublicCodes === true}
                        onChange={e => setPlansPublicCodes(e.target.checked)}
                    />
                    <Form.Text className="text-muted d-block public-placement-fields__hint">
                        Då behöver de som skannar koden information om hur deras personuppgifter hanteras.
                    </Form.Text>
                </Card.Body>
            </Card>

            {plansPublicCodes && (
                <PublicReportPrivacyCard
                    policy={privacy.policy}
                    saving={privacy.saving}
                    publicCodeCount={publicCodeCount}
                    onSave={privacy.save}
                    onClear={privacy.clear}
                />
            )}

            {/* Generate QR Card */}
            <Card className="std-box dropshadow mb-4" style={{ border: 'none' }}>
                <Card.Body style={{ padding: '20px' }}>
                    <h5 style={{ fontSize: '16px', fontWeight: 500, marginBottom: '16px' }}>Skapa ny QR-kod</h5>
                    <div className="d-flex flex-column gap-3">
                        <Form.Group>
                            <Form.Label style={{ fontSize: '14px' }}>Välj byggnad</Form.Label>
                            <Form.Select
                                value={selectedBuildingKey}
                                onChange={e => setSelectedBuildingKey(e.target.value)}
                                disabled={creatingToken}
                            >
                                <option value="">Välj byggnad...</option>
                                {availableBuildings.map(building => (
                                    <option key={building.key} value={building.key}>
                                        {building.name || building.key}
                                    </option>
                                ))}
                            </Form.Select>
                            {availableBuildings.length === 0 && (
                                <Form.Text className="text-muted">
                                    Alla byggnader har redan en QR-kod.
                                </Form.Text>
                            )}
                        </Form.Group>
                        <PublicPlacementFields
                            idPrefix="create-qr"
                            value={createPlacement}
                            onChange={handleCreatePlacementChange}
                            hasPolicy={Boolean(privacy.policy)}
                            disabled={creatingToken}
                        />
                        <Button
                            variant="primary"
                            onClick={handleCreateQrCode}
                            disabled={!selectedBuildingKey || creatingToken || !isPlacementComplete(createPlacement)}
                            className="d-flex align-items-center gap-2 align-self-start"
                        >
                            {creatingToken ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <Plus size={18} />
                            )}
                            Skapa QR-kod
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {/* Active QR Codes List */}
            <Card className="std-box dropshadow" style={{ border: 'none' }}>
                <Card.Body style={{ padding: '20px' }}>
                    <h5 style={{ fontSize: '16px', fontWeight: 500, marginBottom: '16px' }}>
                        Aktiva QR-koder ({activeTokens.length})
                    </h5>

                    {loadingTokens ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                            <p className="mt-2 text-muted small">Laddar...</p>
                        </div>
                    ) : activeTokens.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            <QrCode size={48} className="mb-3 opacity-50" />
                            <p>Inga aktiva QR-koder finns ännu.</p>
                            <p className="small">Använd formuläret ovan för att skapa en QR-kod för en byggnad.</p>
                        </div>
                    ) : (
                        <Table hover responsive className="mb-0 qr-token-table">
                            <thead>
                                <tr>
                                    <th>Byggnad</th>
                                    <th>Adress</th>
                                    <th>Skapad</th>
                                    <th style={{ width: 100 }}>Åtgärder</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeTokens.map(token => (
                                    <tr
                                        key={token.token}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleOpenManageModal(token)}
                                    >
                                        <td className="qr-token-building">
                                            <div className="d-flex align-items-center flex-wrap gap-2">
                                                <QrCode size={16} className="text-success flex-shrink-0" />
                                                {token.buildingName}
                                                {token.publicPlacement && (
                                                    <span className="badge bg-info text-dark d-inline-flex align-items-center gap-1">
                                                        <Users size={12} />
                                                        Allmän plats
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-muted qr-token-address">{token.buildingAddress || '-'}</td>
                                        <td className="text-muted qr-token-created">{token.createdAt.split(' ')[0]}</td>
                                        {/* Phones hide this: the whole row already opens the QR code */}
                                        <td className="qr-token-actions" onClick={e => e.stopPropagation()}>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                onClick={() => handleOpenManageModal(token)}
                                                title="Hantera"
                                            >
                                                <Eye size={16} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Manage QR Modal */}
            <Modal show={showManageModal} onHide={() => setShowManageModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Hantera QR-kod</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedToken && (
                        <>
                            <div className="mb-3 p-3 bg-light rounded">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <QrCode size={24} className="text-success" />
                                    <strong>QR-kod aktiv</strong>
                                </div>
                                <p className="small text-muted mb-0">
                                    {selectedToken.buildingName}
                                    {selectedToken.buildingAddress && <><br />{selectedToken.buildingAddress}</>}
                                </p>
                                <p className="small mb-0 mt-2">
                                    {selectedToken.publicPlacement
                                        ? 'Placerad på allmän plats: integritetspolicyn visas för den som skannar.'
                                        : 'Intern kod: ingen integritetsinformation visas.'}
                                </p>
                            </div>

                            <div className="d-flex flex-column gap-2">
                                <Button
                                    variant="primary"
                                    onClick={handleDownloadPdf}
                                    disabled={generatingPdf || actionLoading}
                                    className="d-flex align-items-center justify-content-center gap-2"
                                >
                                    {generatingPdf ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <Download size={18} />
                                    )}
                                    Ladda ner PDF
                                </Button>

                                <Button
                                    variant="outline-secondary"
                                    onClick={handlePreviewPdf}
                                    disabled={generatingPdf || actionLoading}
                                    className="d-flex align-items-center justify-content-center gap-2"
                                >
                                    <Eye size={18} />
                                    Förhandsgranska
                                </Button>

                                <hr className="my-2" />

                                <Button
                                    variant="outline-warning"
                                    onClick={handleRegenerateClick}
                                    disabled={actionLoading}
                                    className="d-flex align-items-center justify-content-center gap-2"
                                >
                                    <RefreshCw size={18} />
                                    Generera ny QR-kod
                                </Button>

                                <Button
                                    variant="outline-danger"
                                    onClick={handleRevokeClick}
                                    disabled={actionLoading}
                                    className="d-flex align-items-center justify-content-center gap-2"
                                >
                                    <Trash2 size={18} />
                                    Ta bort QR-kod
                                </Button>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowManageModal(false)}>
                        Stäng
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Bekräfta</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className={confirmAction === 'regenerate' ? 'mb-3' : 'mb-0'}>
                        Är du säker på att du vill {confirmAction === 'revoke' ? 'ta bort' : 'generera en ny'} QR-kod?
                        Befintliga utskrivna QR-koder kommer att sluta fungera.
                    </p>
                    {confirmAction === 'regenerate' && (
                        <PublicPlacementFields
                            idPrefix="regenerate-qr"
                            value={regeneratePlacement}
                            onChange={handleRegeneratePlacementChange}
                            hasPolicy={Boolean(privacy.policy)}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Avbryt
                    </Button>
                    <Button
                        variant={confirmAction === 'revoke' ? 'danger' : 'warning'}
                        onClick={handleConfirm}
                        disabled={confirmAction === 'regenerate' && !isPlacementComplete(regeneratePlacement)}
                    >
                        {confirmAction === 'revoke' ? 'Ta bort' : 'Generera ny'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Remove Logo Confirmation Modal */}
            <Modal show={showRemoveModal} onHide={() => setShowRemoveModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Bekräfta borttagning</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Är du säker på att du vill ta bort logotypen?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
                        Avbryt
                    </Button>
                    <Button variant="danger" onClick={confirmRemove}>
                        Ta bort
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PublicReportSettings;
